import { useEffect, useState } from "react";
import { ArrowDownAZ, ArrowUpZA, Search, X } from "lucide-react";
import { SORT_OPTIONS, STATUS_OPTIONS } from "@/lib/organizer";

const field = "w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none";

export const OrganizerToolbar = ({ settings, students, subjects, showStudent, defaultSort, defaultOrder, onChange, onReset }) => {
  const [search, setSearch] = useState(settings.q);
  useEffect(() => { setSearch(settings.q); }, [settings.q]);
  useEffect(() => {
    const timer = setTimeout(() => { if (search !== settings.q) onChange({ q: search }); }, 300);
    return () => clearTimeout(timer);
  }, [search, settings.q, onChange]);

  const sort = settings.sort || defaultSort;
  const order = settings.order || defaultOrder;
  const filtered = Boolean(settings.q || settings.subject || settings.status || settings.due_from || settings.due_to || settings.sort || settings.include_archived || (showStudent && settings.student_id));

  return (
    <div className="space-y-2" data-testid="organizer-toolbar">
      <label className="relative block">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="organizer-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by assignment title…" className={`${field} pl-9`} />
      </label>
      <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 ${showStudent ? "lg:grid-cols-7" : "lg:grid-cols-6"}`}>
        {showStudent && (
          <select data-testid="organizer-student" value={settings.student_id} onChange={(event) => onChange({ student_id: event.target.value })} className={field}>
            <option value="">All students</option>
            {students.map((student) => <option key={student.id} value={student.id}>{student.name}</option>)}
          </select>
        )}
        <select data-testid="organizer-subject" value={settings.subject} onChange={(event) => onChange({ subject: event.target.value })} className={field}>
          <option value="">All subjects</option>
          {subjects.map((subject) => <option key={subject.slug} value={subject.slug}>{subject.title}</option>)}
        </select>
        <select data-testid="organizer-status" value={settings.status} onChange={(event) => onChange({ status: event.target.value })} className={field}>
          {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <input data-testid="organizer-due-from" type="date" aria-label="Due from" value={settings.due_from} onChange={(event) => onChange({ due_from: event.target.value })} className={field} />
        <input data-testid="organizer-due-to" type="date" aria-label="Due to" value={settings.due_to} onChange={(event) => onChange({ due_to: event.target.value })} className={field} />
        <div className="flex gap-1 col-span-2 sm:col-span-3 lg:col-span-2">
          <select data-testid="organizer-sort" value={sort} onChange={(event) => onChange({ sort: event.target.value, order })} className={field}>
            {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <button type="button" data-testid="organizer-order" aria-label={order === "asc" ? "Ascending — click for descending" : "Descending — click for ascending"} onClick={() => onChange({ sort, order: order === "asc" ? "desc" : "asc" })}
            className="shrink-0 grid place-items-center w-11 rounded-xl border border-slate-200 bg-white text-slate-600 hover:text-teal-800">
            {order === "asc" ? <ArrowDownAZ size={16} /> : <ArrowUpZA size={16} />}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {settings.view === "all" && (
          <label className="inline-flex items-center gap-2 text-slate-600 font-semibold cursor-pointer">
            <input data-testid="organizer-include-archived" type="checkbox" checked={settings.include_archived} onChange={(event) => onChange({ include_archived: event.target.checked })} className="w-4 h-4 accent-teal-700" />
            Include archived
          </label>
        )}
        {filtered && (
          <button type="button" data-testid="organizer-reset" onClick={onReset} className="inline-flex items-center gap-1 text-slate-500 hover:text-teal-800 font-semibold"><X size={12} /> Clear filters</button>
        )}
      </div>
    </div>
  );
};
